import { Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { rxResourceFixed } from '@zayan-erp/shared';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ButtonGroup } from '@openng/optimus-ui/buttongroup';
import { Message } from '@openng/optimus-ui/message';
import { of } from 'rxjs';
import {
  DsmOptionConfigForm,
  DsmProductAttributeGroup,
  DsmProductAttributesResponse,
  DsmKitProductLocationsGroup,
  DsmProductOption,
  DsmSelectedProductLinkedValues,
  DsmSelectedProductValues
} from '../../../models/dsm-deal-options.models';
import { DsmModelElevationProductsService } from '../../../services/dsm-model-elevation-products.service';
import { DsmOptionConfigFieldsComponent } from '../dsm-option-config-fields/dsm-option-config-fields.component';
import { DsmSelectedProductPanelComponent } from '../dsm-selected-product-panel/dsm-selected-product-panel.component';
import { LocationItem } from '@zayan-erp/home-building';

interface DsmLinkedConfigView {
  product: DsmProductOption;
  values: DsmSelectedProductValues;
  attributes: DsmProductAttributeGroup[];
  locations: LocationItem[] | null;
  index: number;
  errorCount: number;
  hasErrors: boolean;
}

@Component({
  selector: 'dsm-kit-option-config-form',
  standalone: true,
  imports: [DsmOptionConfigFieldsComponent, DsmSelectedProductPanelComponent, ButtonModule, ButtonGroup, Message],
  templateUrl: './dsm-kit-option-config-form.component.html'
})
export class DsmKitOptionConfigFormComponent {
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);
  readonly #modelElevationProductsService = inject(DsmModelElevationProductsService);

  readonly product = input.required<DsmProductOption>();
  readonly addendumId = input<string | null>(null);
  // Location source before an addendum exists (appointment screens); ignored when addendumId is set.
  readonly lotId = input<string | null>(null);
  readonly initialValues = input<DsmSelectedProductValues | null>(null);

  readonly commit = output<DsmSelectedProductValues>();

  readonly activeTab = signal<'setup' | 'linked'>('setup');
  readonly submitAttempted = signal(false);
  readonly applyToAllKitOptions = signal(false);

  readonly kitForm = signal<DsmOptionConfigForm | null>(null);
  // Per-linked-product configuration, index-aligned with product().linkedProducts.
  readonly linkedValues = signal<DsmSelectedProductValues[]>([]);

  // Bumped on kit form value changes so totals/validity recompute.
  readonly #formVersion = signal(0);
  #formsBuilt = false;

  readonly #attributesResource = rxResourceFixed({
    params: () => ({ productId: this.product().productId }),
    defaultValue: null as DsmProductAttributesResponse | null,
    stream: ({ params }) => {
      if (!params.productId) return of<DsmProductAttributesResponse | null>(null);
      return this.#modelElevationProductsService.getProductAttributesGrouped(params.productId);
    }
  });

  readonly attributesLoaded = computed(
    () => !this.#attributesResource.isLoading() && this.#attributesResource.value() != null
  );
  readonly ownAttributes = computed<DsmProductAttributeGroup[]>(
    () => this.#attributesResource.value()?.attributes ?? []
  );
  readonly kitInitialAttributeValues = computed<Record<string, string>>(
    () => this.initialValues()?.attributeValues ?? {}
  );

  readonly #linkedAttributesByProductId = computed(() => {
    const map = new Map<string, DsmProductAttributeGroup[]>();
    (this.#attributesResource.value()?.linkedProducts ?? []).forEach(linked => {
      map.set(linked.productId, linked.attributes ?? []);
    });
    return map;
  });

  // One combined call for the parent's own locations plus every linked (kit child) product's
  // locations, instead of a separate /locations request per linked product.
  readonly #kitLocationsResource = rxResourceFixed({
    params: () => ({ productId: this.product().productId, addendumId: this.addendumId(), lotId: this.lotId() }),
    defaultValue: [] as DsmKitProductLocationsGroup[],
    stream: ({ params }) => {
      if (!params.productId) return of<DsmKitProductLocationsGroup[]>([]);
      return this.#modelElevationProductsService.getScopedKitProductLocations(params, params.productId);
    }
  });

  readonly linkedLocationsLoaded = computed(() => !this.#kitLocationsResource.isLoading());

  // The response's first entry is always the parent (kit) product itself.
  readonly parentLocations = computed<LocationItem[] | null>(() => {
    if (!this.linkedLocationsLoaded()) return null;
    return (
      this.#kitLocationsResource.value().find(group => group.productId === this.product().productId)?.locations ?? []
    );
  });

  readonly #linkedLocationsByProductId = computed(() => {
    const map = new Map<string, LocationItem[]>();
    this.#kitLocationsResource.value().forEach(group => map.set(group.productId, group.locations ?? []));
    return map;
  });

  readonly #linkedInitialByMep = computed(() => {
    const map = new Map<string, DsmSelectedProductLinkedValues>();
    (this.initialValues()?.linkedProducts ?? []).forEach(linked => map.set(linked.modelElevationProductsId, linked));
    return map;
  });

  readonly linkedViews = computed<DsmLinkedConfigView[]>(() => {
    const values = this.linkedValues();
    const attributeMap = this.#linkedAttributesByProductId();
    const loaded = this.attributesLoaded();
    const locationsLoaded = this.linkedLocationsLoaded();
    const locationsMap = this.#linkedLocationsByProductId();

    return this.product().linkedProducts.map((linkedProduct, index) => {
      const productValues = values[index] ?? this.#defaultLinkedValues(linkedProduct);
      const groups = linkedProduct.productId ? (attributeMap.get(linkedProduct.productId) ?? []) : [];
      const locations = locationsLoaded
        ? linkedProduct.productId
          ? (locationsMap.get(linkedProduct.productId) ?? [])
          : []
        : null;

      let errorCount = 0;
      if (loaded && linkedProduct.selectionRequired) {
        errorCount = groups.length
          ? groups.filter(group => !productValues.attributeValues[group.subCategoryAttributeId]).length
          : 1;
      }

      return {
        product: linkedProduct,
        values: productValues,
        attributes: groups,
        locations,
        index,
        errorCount,
        hasErrors: errorCount > 0
      };
    });
  });

  readonly linkedMissingTotal = computed(() => this.linkedViews().reduce((total, view) => total + view.errorCount, 0));

  readonly invalid = computed(() => {
    this.#formVersion();
    const kitForm = this.kitForm();
    if (!kitForm || kitForm.invalid) return true;
    return this.linkedMissingTotal() > 0;
  });

  readonly totalQty = computed(() => {
    this.#formVersion();
    return this.kitForm()?.controls.quantity.value ?? 0;
  });

  readonly totalPrice = computed(() => {
    this.#formVersion();
    const kitForm = this.kitForm();
    let total = (kitForm?.controls.quantity.value ?? 0) * (kitForm?.controls.unitPrice.value ?? 0);
    this.linkedValues().forEach(values => {
      total += (values.quantity ?? 0) * (values.unitPrice ?? 0);
    });
    return total;
  });

  constructor() {
    // Build the kit form and seed the linked product values once the product is available.
    effect(() => {
      const product = this.product();
      if (this.#formsBuilt) return;
      this.#formsBuilt = true;

      const init = this.initialValues();
      const kitForm = this.#fb.group({
        // Location is optional (matches PR #2061 removing the location-required check).
        locationId: this.#fb.control<string | null>(init?.locationId ?? null),
        quantity: this.#fb.control<number | null>(init?.quantity ?? product.quantity ?? 1, [Validators.required]),
        unitPrice: this.#fb.control<number | null>(init?.unitPrice ?? product.unitPrice ?? null, [Validators.required]),
        notes: this.#fb.nonNullable.control(init?.notes ?? ''),
        attributeValues: this.#fb.record<FormControl<string | null>>({})
      }) as DsmOptionConfigForm;

      const linkedInitial = this.#linkedInitialByMep();
      const seededLinked = product.linkedProducts.map(linkedProduct => {
        const li = linkedInitial.get(linkedProduct.modelElevationProductsId);
        return li
          ? {
              locationId: li.locationId,
              quantity: li.quantity,
              unitPrice: li.unitPrice,
              notes: li.notes,
              attributeValues: { ...li.attributeValues }
            }
          : this.#defaultLinkedValues(linkedProduct);
      });

      this.kitForm.set(kitForm);
      this.linkedValues.set(seededLinked);

      kitForm.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => this.#formVersion.update(version => version + 1));
    });

    // "Apply to all Kit Options": mirror the kit's location onto every linked product.
    effect(() => {
      this.#formVersion();
      if (!this.applyToAllKitOptions()) return;
      this.#propagateKitLocation();
    });
  }

  onApplyToAllKitOptionsChange(checked: boolean): void {
    this.applyToAllKitOptions.set(checked);
    if (checked) this.#propagateKitLocation();
  }

  onCopyLocationToLinked(): void {
    const kitForm = this.kitForm();
    if (!kitForm) return;

    const locationId = kitForm.controls.locationId.value;
    if (!locationId) return;

    const locationsByProductId = this.#linkedLocationsByProductId();

    this.linkedValues.update(current =>
      this.product().linkedProducts.map((linkedProduct, index) => {
        const values = current[index] ?? this.#defaultLinkedValues(linkedProduct);
        const locations = linkedProduct.productId ? (locationsByProductId.get(linkedProduct.productId) ?? []) : [];
        const locationExists = locations.some(location => location.id === locationId);
        return locationExists ? { ...values, locationId } : values;
      })
    );
  }

  onCopyQuantityToLinked(): void {
    const kitForm = this.kitForm();
    if (!kitForm) return;

    const quantity = kitForm.controls.quantity.value;
    if (!quantity) return;

    this.linkedValues.update(current =>
      this.product().linkedProducts.map((linkedProduct, index) => {
        const values = current[index] ?? this.#defaultLinkedValues(linkedProduct);
        return { ...values, quantity };
      })
    );
  }

  onCopyAttributesToLinked(attributeId: string): void {
    const kitForm = this.kitForm();
    if (!kitForm) return;

    const kitValueId = kitForm.controls.attributeValues.get(attributeId)?.value;
    if (!kitValueId) return;

    const attributeMap = this.#linkedAttributesByProductId();

    this.linkedValues.update(current =>
      this.product().linkedProducts.map((linkedProduct, index) => {
        const values = current[index] ?? this.#defaultLinkedValues(linkedProduct);
        const groups = linkedProduct.productId ? (attributeMap.get(linkedProduct.productId) ?? []) : [];

        const group = groups.find(g => g.subCategoryAttributeId === attributeId);
        if (!group) return values;

        const valueExists = group.productAttributeValues.some(value => value.id === kitValueId);
        if (!valueExists) return values;

        return { ...values, attributeValues: { ...values.attributeValues, [attributeId]: kitValueId } };
      })
    );
  }

  onLinkedEditCommit(values: DsmSelectedProductValues, index: number): void {
    this.linkedValues.update(current => current.map((value, i) => (i === index ? values : value)));
  }

  attemptCommit(): void {
    this.submitAttempted.set(true);
    const kitForm = this.kitForm();
    if (!kitForm) return;

    kitForm.markAllAsTouched();
    if (kitForm.invalid) {
      this.activeTab.set('setup');
      return;
    }
    if (this.linkedMissingTotal() > 0) {
      this.activeTab.set('linked');
      return;
    }

    const kitRaw = kitForm.getRawValue();
    const linkedProducts: DsmSelectedProductLinkedValues[] = this.product().linkedProducts.map(
      (linkedProduct, index) => {
        const values = this.linkedValues()[index] ?? this.#defaultLinkedValues(linkedProduct);
        return {
          modelElevationProductsId: linkedProduct.modelElevationProductsId,
          locationId: values.locationId,
          quantity: values.quantity,
          unitPrice: values.unitPrice,
          notes: values.notes,
          attributeValues: values.attributeValues
        };
      }
    );

    this.commit.emit({
      locationId: kitRaw.locationId,
      quantity: kitRaw.quantity ?? 1,
      unitPrice: kitRaw.unitPrice ?? 0,
      notes: kitRaw.notes ?? '',
      attributeValues: this.#extractAttributes(kitForm),
      linkedProducts
    });
  }

  #propagateKitLocation(): void {
    const kitForm = this.kitForm();
    if (!kitForm) return;
    const locationId = kitForm.controls.locationId.value;
    this.linkedValues.update(current => current.map(values => ({ ...values, locationId })));
  }

  #defaultLinkedValues(linkedProduct: DsmProductOption): DsmSelectedProductValues {
    return {
      locationId: null,
      quantity: linkedProduct.quantity || 1,
      unitPrice: linkedProduct.unitPrice ?? 0,
      notes: '',
      attributeValues: {}
    };
  }

  #extractAttributes(form: DsmOptionConfigForm): Record<string, string> {
    const result: Record<string, string> = {};
    Object.entries(form.controls.attributeValues.controls).forEach(([id, control]) => {
      if (control.value) result[id] = control.value;
    });
    return result;
  }
}
